import { useDispatch, useRootStore, useTranslation, useUserStore } from "@/stores/RootStoreContext";
import { Icon } from "@/components-v2/Icon";
import { Box, Container, Grid, VBox } from "@/components-v2/Box";
import { FC, useEffect, useMemo } from "react";
import { isCancelled, isFailure, isPending, Result } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { observer } from "mobx-react";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { MTextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { useAppBar } from "@/appBar/useAppBar";
import { percentOf } from "@/utils/numbers";
import { totalNumberOfShares } from "@/types/Shareholder";
import { Dialog } from "@/components-v2/Dialog";
import { MDateInput } from "@/components-v2/DateInput";
import { TMessage } from "@/i18n/tt";
import { LocalDateFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import {
  ShareholderCommitment,
  ShareholderCommitmentType,
  shareholderCommitmentTypes,
} from "@/shareholderRegister/shareholderRegisterDomain";
import {
  commitmentRegisterLabel,
  CommitmentRow,
  filterCommitmentRows,
  prepareCommitmentOverviewRows,
  prepareCommitmentOverviewTotal,
  prepareCommitmentRows,
  prepareShareholderCommitmentsEmptyState,
  prepareShareholderCommitmentsTable,
  ShareholderCommitmentController,
  ShareholderCommitmentData,
  ShareholderCommitmentSubpage,
  ShareholderCommitmentTotalData,
  shareholderCommitmentTypeLabel,
} from "./shareholderCommitmentsDomain";
import {
  adminShareholderDetails,
  adminShareholdersNewShareholderCommitment,
  adminShareholdersShareholderCommitmentLog,
  adminShareholdersShareholderCommitments,
  navigateToNewShareholderCommitment,
  navigateToShareholderCommitmentDetails,
  navigationState,
} from "@/utils/navigation";
import { DataCard } from "@/components-v2/DataCard";
import { EmptyState } from "@/components-v2/EmptyState";
import { MenuItem } from "@/components-v2/MenuBar";

export const ShareholderCommitmentsPage: FC<{ activePage: ShareholderCommitmentSubpage }> =
  observer(function ShareholderCommitmentsPage({ activePage }) {
    const { t } = useTranslation();
    const appBar = useAppBar();
    const { apiService, messageStore, userStore, shareholdersStore } = useRootStore();
    const userRole = userStore.userRole;
    const canEdit = userRole?.allowCommitmentsEdit ?? true;

    useEffect(() => {
      shareholdersStore.loadAllShareholders();
    }, [shareholdersStore]);

    const controller = useMemo(
      () => new ShareholderCommitmentController(apiService, messageStore, userStore.orgNumber),
      [apiService, messageStore, userStore.orgNumber],
    );
    useEffect(() => {
      controller.fetchShareholderCommitments();
    }, [controller]);

    function render(result?: Result<ShareholderCommitment[]>) {
      if (result === undefined || isPending(result)) {
        return (
          <VBox alignItems={"center"} grow={"golden"} justifyContent={"center"}>
            <LoadingIndicator />
          </VBox>
        );
      }
      if (isFailure(result) || isCancelled(result)) {
        return (
          <VBox grow={"golden"} justifyContent={"center"}>
            <LoadingError />
          </VBox>
        );
      }
      if (result.data.length === 0) {
        return (
          <VBox grow={"golden"} justifyContent={"center"}>
            <EmptyState
              {...prepareShareholderCommitmentsEmptyState({
                action: canEdit
                  ? {
                      variant: "secondary",
                      label: { key: commitmentRegisterLabel("COMMITMENT") },
                      icon: "plus",
                      onClick: [
                        {
                          type: "navigate",
                          url: adminShareholdersNewShareholderCommitment(
                            userStore.orgNumber,
                            "COMMITMENT",
                          ),
                        },
                      ],
                    }
                  : undefined,
              })}
            />
          </VBox>
        );
      }

      const commitmentOverviewRows = prepareCommitmentOverviewRows(
        result.data,
        shareholdersStore.shareholders,
        totalNumberOfShares(shareholdersStore.shareSumBySharetypeCode),
      );
      const total = prepareCommitmentOverviewTotal(
        result.data,
        commitmentOverviewRows,
        shareholdersStore.currency,
      );
      const isDrawDisabled = total.remaining > 0;
      return activePage === "overview" ? (
        <ShareholderCommitmentsOverview
          canEdit={canEdit}
          data={commitmentOverviewRows}
          total={total}
          isDrawDisabled={isDrawDisabled}
        />
      ) : (
        <ShareholderCommitmentsTable
          canEdit={canEdit}
          data={prepareCommitmentRows(result.data, shareholdersStore.activeShareholders, t)}
          textFilter={controller.textFilter}
          typeFilter={controller.typeFilter}
          controller={controller}
          isDrawDisabled={isDrawDisabled}
        />
      );
    }

    return (
      <FullScreenLayout
        appBar={appBar}
        leftPanel={<LeftPanel activePage={activePage} orgNumber={userStore.orgNumber} />}
        centerPanel={render(controller.fetchShareholderCommitmentsResult)}
      />
    );
  });

const LeftPanel = observer(function LeftPanel({
  orgNumber,
  activePage,
}: {
  orgNumber: string;
  activePage: ShareholderCommitmentSubpage;
}) {
  return (
    <VBox htmlProps={{ style: { minWidth: "300px" } }} padding={"md"}>
      <MenuItem
        icon={"users"}
        text={{ key: "shareholder-commitments.commitments.heading" }}
        url={adminShareholdersShareholderCommitments(orgNumber)}
        isActive={activePage === "overview"}
      />
      <MenuItem
        icon={"clock-counter-clockwise"}
        text={{ key: "shareholder-commitments.log.heading" }}
        url={adminShareholdersShareholderCommitmentLog(orgNumber)}
        isActive={activePage === "log"}
      />
    </VBox>
  );
});

function LoadingError() {
  const { t } = useTranslation();
  return (
    <VBox alignItems={"center"}>
      <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
      <Box fg={"error"}>{t("shareholder-commitments.fetch-error")}</Box>
    </VBox>
  );
}

const ShareholderCommitmentsOverview = observer(function ShareholderCommitmentsOverview({
  data,
  total,
  canEdit,
  isDrawDisabled,
}: {
  data: ShareholderCommitmentData[];
  total: ShareholderCommitmentTotalData;
  canEdit: boolean;
  isDrawDisabled: boolean;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname: currentUrl } = useRootStore().location;
  const userStore = useUserStore();
  const columns: TableColumn<ShareholderCommitmentData>[] = [
    {
      name: "shareholder",
      label: { key: "shareholder-commitments.shareholder" },
      align: "left",
    },
    {
      name: "ownership",
      label: { key: "shareholder-commitments.ownership" },
      getValue: (row) => ({ type: "percent", value: row.ownership }),
    },
    {
      name: "committed",
      label: { key: "shareholder-commitments.committed" },
      getValue: (row) => ({ type: "amount", value: row.committed }),
    },
    {
      name: "drawn",
      label: { key: "shareholder-commitments.drawn" },
      getValue: (row) => ({ type: "amount", value: row.drawn }),
    },
    {
      name: "distributed",
      label: { key: "shareholder-commitments.distributed" },
      getValue: (row) => ({ type: "amount", value: row.distributed }),
    },
    {
      name: "recallable",
      label: { key: "shareholder-commitments.recallable" },
      getValue: (row) => ({ type: "amount", value: row.recallable }),
    },
    {
      name: "remaining",
      label: { key: "shareholder-commitments.remaining" },
      getValue: (row) => ({ type: "amount", value: row.remaining }),
    },
  ];

  const orgNo = userStore.orgNumber;

  return (
    <Container marginTop={"md"}>
      <VBox gap={"xl"} marginTop={"lg"} marginX={"md"}>
        <Box>
          <Box grow>
            <h2>{t("shareholder-commitments.overview.heading")}</h2>
          </Box>
          <Box>
            {canEdit && <NewCommitmentButton orgNo={orgNo} isDrawDisabled={isDrawDisabled} />}
          </Box>
        </Box>
        <ShareholderCommitmentsOverviewTotal total={total} />
        <DataTable<ShareholderCommitmentData>
          tableColumns={columns}
          tableData={data}
          onRowClick={(row) =>
            dispatch([
              {
                type: "navigate",
                url: adminShareholderDetails(orgNo, row.id, "commitments"),
                state: navigationState({ referrer: currentUrl }),
              },
            ])
          }
          getId={(row) => row.id}
        />
      </VBox>
    </Container>
  );
});

export const ShareholderCommitmentsOverviewTotal = observer(
  function ShareholderCommitmentsOverviewTotal({
    total,
  }: {
    total: ShareholderCommitmentTotalData;
  }) {
    return (
      <Grid cols={4} gap={"lg"}>
        <DataCard
          title={{ key: "shareholder-commitments.total" }}
          subtitle={{ translated: total.currency }}
          infoPopover={{ content: { key: "shareholder-commitments.total.help-text" } }}
          extraInfo={
            total.lastRegistrationDate
              ? {
                  key: "shareholder-commitments.total.committed.extraText",
                  params: { date: { type: "date", value: total.lastRegistrationDate } },
                }
              : undefined
          }
          value={{
            type: "number",
            value: total.committed,
            maximumFractionDigits: 2,
          }}
        />
        <DataCard
          title={{ key: "shareholder-commitments.drawn" }}
          subtitle={{ translated: total.currency }}
          infoPopover={{ content: { key: "shareholder-commitments.drawn.help-text" } }}
          extraInfo={{
            key: "shareholder-commitments.total.of-committed-capital.extraText",
            params: {
              percent: {
                type: "percent",
                value: percentOf(total.drawn, total.committed),
              },
            },
          }}
          value={{
            type: "number",
            value: total.drawn,
            maximumFractionDigits: 2,
          }}
        />
        <DataCard
          title={{ key: "shareholder-commitments.distributed" }}
          subtitle={{ translated: total.currency }}
          infoPopover={{ content: { key: "shareholder-commitments.distributed.help-text" } }}
          extraInfo={
            total.distributed > 0
              ? {
                  key: "shareholder-commitments.total.distributed.extraText",
                  params: {
                    value: {
                      type: "number",
                      value: total.recallable,
                      maximumFractionDigits: 2,
                    },
                  },
                }
              : undefined
          }
          value={{
            type: "number",
            value: total.distributed,
            maximumFractionDigits: 2,
          }}
        />
        <DataCard
          title={{ key: "shareholder-commitments.remaining" }}
          subtitle={{ translated: total.currency }}
          infoPopover={{ content: { key: "shareholder-commitments.remaining.help-text" } }}
          extraInfo={{
            key: "shareholder-commitments.total.of-committed-capital.extraText",
            params: {
              percent: {
                type: "percent",
                value: percentOf(total.remaining, total.committed),
              },
            },
          }}
          value={{
            type: "number",
            value: total.remaining,
            maximumFractionDigits: 2,
          }}
        />
      </Grid>
    );
  },
);

const ShareholderCommitmentsTable = observer(function ShareholderCommitmentsTable({
  data,
  textFilter,
  typeFilter,
  controller,
  canEdit,
  isDrawDisabled,
}: {
  data: CommitmentRow[];
  textFilter: StringFieldModel;
  typeFilter: RadioFieldModel<ShareholderCommitmentType | null>;
  controller: ShareholderCommitmentController;
  canEdit: boolean;
  isDrawDisabled: boolean;
}) {
  const { t } = useTranslation();
  const userStore = useUserStore();
  const history = useRootStore().history;
  const orgNo = userStore.orgNumber;
  return (
    <Container>
      <VBox gap={"md"} marginTop={"md"}>
        <Box>
          <Box grow>
            <h2>{t("shareholder-commitments.log.heading")}</h2>
          </Box>
          <Box>
            <NewCommitmentButton orgNo={orgNo} isDrawDisabled={isDrawDisabled} />
          </Box>
        </Box>
        <Box>
          <Box>
            <MTextInput model={textFilter} placeholder={t("search")} type={"search"} />
          </Box>
          <Box>
            <MSelect model={typeFilter} />
          </Box>
        </Box>
        <DataTable
          {...prepareShareholderCommitmentsTable({
            data: filterCommitmentRows(data, textFilter.inputValue, typeFilter.value),
            canEdit,
            changeDate: controller.showChangeDateDialogForCommitment,
            deleteCommitment: controller.showDeleteConfirmationDialogForCommitment,
            navigateToDetails: (id) => navigateToShareholderCommitmentDetails(orgNo, id, history),
          })}
        />
      </VBox>
      <ShareholderCommitmentsDialogs controller={controller} />
    </Container>
  );
});

export const ShareholderCommitmentsDialogs = observer(function ShareholderCommitmentsDialogs({
  controller,
}: {
  controller: ShareholderCommitmentController;
}) {
  return (
    <>
      {controller.isShowingChangeDateDialog && (
        <ChangeDateDialog
          onCancel={controller.hideChangeDateDialog}
          onSubmit={controller.submitChangeDate}
          dateModel={controller.changeDateModel}
          submitResult={controller.submitChangeDateResult}
        />
      )}
      {controller.isShowingDeleteConfirmationDialog && (
        <DeleteConfirmationDialog
          onCancel={controller.hideDeleteConfirmationDialog}
          onSubmit={controller.submitDelete}
          submitResult={controller.submitDeleteResult}
        />
      )}
    </>
  );
});

function ChangeDateDialog({
  onCancel,
  onSubmit,
  dateModel,
  submitResult,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  dateModel: LocalDateFieldModel;
  submitResult?: Result<TMessage, TMessage>;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("shareholder-commitments.change-date")}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitResult={submitResult}
      maxWidth={"sm"}
    >
      <VBox marginTop={"sm"}>
        <MDateInput model={dateModel} />
      </VBox>
    </Dialog>
  );
}

function DeleteConfirmationDialog({
  onCancel,
  onSubmit,
  submitResult,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitResult?: Result<TMessage, TMessage>;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("shareholder-commitments.delete")}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitResult={submitResult}
      maxWidth={"sm"}
      submitLabel={t("shareholder-commitments.delete")}
    >
      <VBox marginTop={"sm"}>
        {submitResult === undefined && <p>{t("shareholder-commitments.delete-confirmation")}</p>}
      </VBox>
    </Dialog>
  );
}

const NewCommitmentButton = observer(function NewCommitmentButton({
  orgNo,
  isDrawDisabled,
}: {
  orgNo: string;
  isDrawDisabled: boolean;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;
  return (
    <DropdownMenu
      button={{
        type: "button",
        variant: "secondary",
        label: t("shareholder-commitments.register"),
        fullWidth: true,
      }}
      items={shareholderCommitmentTypes
        .filter((d) => isDrawDisabled || d !== "DRAW")
        .map((type) => ({
          type: "item",
          label: t(shareholderCommitmentTypeLabel(type)),
          onSelect: () => navigateToNewShareholderCommitment(orgNo, history, type),
        }))}
      alignment={"end"}
    />
  );
});
